<script setup lang="ts">

import { computed, onMounted, ref } from "vue";
import { useViewModelInstance } from "@app/MyGarage";
import { useParams } from "@library/Utilities";
import { useCarePlanApi } from "@app/MyGarage/Types/Api/CarePlanApi";
import { formatDate } from "@library/Utilities";
import { ConditionType } from "@domain/Vehicle";
import { WorkOrderType } from "@domain/CarePlan/WorkOrders/WorkOrderType";
import { FeatureType } from "@domain/CarePlan/WorkOrders/FeatureType";

import ConfigureCondition from "../ConfigureCondition/ConfigureCondition.vue";
import TyreRepairFeature from "./Features/TyreRepairFeature.vue";
import TyreReplacementFeature from "./Features/TyreReplacementFeature.vue";
import BrakesRepairFeature from "./Features/BrakesRepairFeature.vue";
import BatteryRepairFeature from "./Features/BatteryRepairFeature.vue";
import WiperBladeRepairFeature from "./Features/WiperBladeRepairFeature.vue";
import KeyFobRepairFeature from "./Features/KeyFobRepairFeature.vue";
import ChippedWindscreenRepairFeature from "./Features/ChippedWindscreenRepairFeature.vue";
import VehicleHealthCheckFeature from "./Features/VehicleHealthCheckFeature.vue";
import { ICarbucksRewardsFeature } from "@domain/CarePlan";
import ProtectStatutoryWarning from "@app/MyGarage/Components/ProtectStatutoryWarning/ProtectStatutoryWarning.vue";

const { itemType, conditionType, manage } = useParams();

const model = await useViewModelInstance();
const item = model.carePlan.getItem( itemType );
const condition = model.vehicle.condition.getCondition( conditionType );
const history = model.carePlan.getItemHistory( itemType ) ?? [];
const isManage = manage != null;
const manageCondition = ref<HTMLElement>();

// Document links
const api = useCarePlanApi();
const documents 
    = item?.product == FeatureType.Rewards ? await api.getCarbucksRewardsDocumentUrls( model.vehicle.vehicleId )
    : item?.product == FeatureType.Protect ? await api.getCarbucksProtectDocumentUrls( model.vehicle.vehicleId )
    : null;

const termsAndConditionsLinkUrl = ref( documents?.termsAndConditionsLinkUrl );
const documentsLinkUrl = ref( documents?.featureSummaryLinkUrl );
const hasDocuments = termsAndConditionsLinkUrl.value != null || documentsLinkUrl.value != null;

const tyreRepair = item?.type == WorkOrderType.TyreRepair ? item
    : item?.type == WorkOrderType.TyreReplacement ? model.carePlan.getItem( WorkOrderType.TyreRepair )
    : null;

const tyreReplacement = item?.type == WorkOrderType.TyreReplacement ? item
    : item?.type == WorkOrderType.TyreRepair ? model.carePlan.getItem( WorkOrderType.TyreReplacement )
    : null;

const featuredProduct = computed( () =>
{
    if( item?.product == FeatureType.Protect )
    {
        return model.carePlan.carbucksProtect;
    }
    return model.carePlan.carbucksRewards;
});

const isProtectCondition = ( condition?.type !== undefined ) ? [ConditionType.Brakes, ConditionType.Tyres, ConditionType.Battery, ConditionType.WiperBlades].includes( condition.type ) : false;
const isProtectWorkOrder = ( item?.type !== undefined ) ? [WorkOrderType.BrakeService, WorkOrderType.TyreRepair, WorkOrderType.TyreReplacement, WorkOrderType.BatteryReplacement, WorkOrderType.WiperBladeReplacement].includes( item.type ) : false;

onMounted( () => {
    if( isManage && manageCondition.value != null )
    {
        manageCondition.value.scrollIntoView();
    }
});

</script>

<template>

    <div class="bg-gray-900 text-white
                text-base lg:text-lg 3xl:text-xl
                w-full h-full overflow-y-auto overflow-x-hidden 
                flex flex-col gap-4 2xl:gap-6 pb-12 pt-4">

        <div v-if="item != null || condition != null"
            class="flex flex-col gap-6 2xl:gap-8
                rounded-tr-xl 2xl:rounded-tr-2xl px-4 mt-5 2xl:mt-6
                bg-gradient-to-b from-gray-850 via-gray-850 via-80% to-gray-900">
            
            <!-- TITLE -->
            <div class="w-full flex flex-col items-center">
                <div class="h-8 2xl:h-10 text-sm -mt-4 2xl:-mt-5 mb-5 tracking-wider px-4 flex justify-center items-center self-start -left-4">
                    <div class="h-full w-full bg-gray-850 absolute bottom-0 rounded-t-xl" />
                    <a class="cursor-pointer text-cyan-500 uppercase flex items-center text-sm 2xl:text-lg -left-1 tracking-wider"
                        @click="$router.go(-1)">
                        <Icon name="chevron-left" />
                        Back
                    </a>
                </div>
                <div v-if="item != null"
                    class="flex flex-col items-center gap-2 2xl:gap-4">
                    <div class="px-8">
                        <img v-if="featuredProduct.type == FeatureType.Protect && featuredProduct.isEnabled" src="@/assets/images/protect-logo.png" class="w-full" />
                        <img v-else-if="
                            featuredProduct.type == FeatureType.Rewards && 
                            model.carePlan.carbucksRewards.isEnabled && 
                            ( featuredProduct as ICarbucksRewardsFeature ).showAsProductInApp" 
                            src="@/assets/images/rewards-logo.png" 
                            class="w-full"
                        />
                    </div>
                </div>
                <span class="uppercase text-white/80 tracking-wider"
                    :class="item != null ? 'text-lg 2xl:text-2xl mt-1.5' : 'text-xl 2xl:text-3xl mt-0'">
                    <span v-if="isManage">Manage </span>{{ condition?.labels.name ?? WorkOrderType.toString( itemType ) }} 
                </span>
            </div>

            <ProtectStatutoryWarning v-if="( isProtectCondition || isProtectWorkOrder ) && model.carePlan && model.carePlan.carbucksProtect" class="text-sm text-center" :is-used="model.carePlan.carbucksProtect.isUsed" :contract-start-date="model.carePlan.carbucksProtect.dateActiveFrom" />

            <!-- WORK ORDER REPAIR OPTIONS -->
            <div v-if="item != null"
                class="flex flex-col gap-4">
            
                <!-- TAB / TITLE -->
                <div class="h-3 mr-auto -mb-4 pt-2 rounded-t-md 2xl:rounded-t-lg bg-gray-700">
                    <div class="h-4 w-4 bg-gray-700 absolute -right-4 bottom-0">
                        <div class="h-4 w-4 bg-gray-850 rounded-bl-[30%]" />
                    </div>
                    <h1 class="text-xs lg:text-base flex items-center gap-x-3 lg:gap-x-4 z-10
                            text-primary-500 dark:text-white px-4 py-1 tracking-widest">
                        <span class="uppercase 2xl:text-lg flex gap-3 items-center">
                            <Icon name="service" />
                            Book a {{ item.type == WorkOrderType.VehicleHealthChecks ? 'health check' : 'repair' }}
                        </span>
                    </h1>
                </div>

                <!-- CONTENT -->
                <div class="gap-2 justify-center items-center
                            bg-gradient-to-b from-gray-700 to-gray-800
                            rounded-b-lg 2xl:rounded-b-xl
                            rounded-tr-lg 2xl:rounded-tr-xl
                            text-sm pb-4 pt-6 ">

                    <!-- TYRE REPAIR -->
                    <div v-if="item?.type == WorkOrderType.TyreRepair || item?.type == WorkOrderType.TyreReplacement"
                        class="flex flex-col gap-4 2xl:gap-6">
                    
                        <!-- TYRE REPAIR -->
                        <TyreRepairFeature
                            v-if="tyreRepair != null"
                            :care-plan="model.carePlan"
                            :item="tyreRepair"
                            :condition="condition"
                            :class="{ 'pb-4 border-b-2 border-gray-850': tyreReplacement != null }"
                        />
                        
                        <!-- TYRE REPLACEMENT -->
                        <TyreReplacementFeature
                            v-if="tyreReplacement != null"
                            :care-plan="model.carePlan"
                            :item="tyreReplacement"
                            :condition="condition"
                        />

                    </div>

                    <!-- BRAKES REPAIR -->
                    <BrakesRepairFeature
                        v-if="item?.type == WorkOrderType.BrakeService" 
                        :care-plan="model.carePlan"
                        :item="item"
                        :condition="condition"
                    />

                    <!-- BATTERY REPAIR -->
                    <BatteryRepairFeature
                        v-if="item?.type == WorkOrderType.BatteryReplacement || condition?.type == ConditionType.Battery" 
                        :care-plan="model.carePlan"
                        :item="item!"
                        :condition="condition"
                    />

                    <!-- WIPER BLADE REPAIR -->
                    <WiperBladeRepairFeature
                        v-if="item?.type == WorkOrderType.WiperBladeReplacement" 
                        :care-plan="model.carePlan"
                        :item="item"
                        :condition="condition"
                    />

                    <!-- KEY FOB REPAIR -->
                    <KeyFobRepairFeature
                        v-if="item?.type == WorkOrderType.KeyFobService" 
                        :care-plan="model.carePlan"
                        :item="item"
                        :condition="condition"
                    />

                    <!-- CHIPPED WINDSCREEN REPAIR -->
                    <ChippedWindscreenRepairFeature
                        v-if="item?.type == WorkOrderType.WindscreenRepair" 
                        :care-plan="model.carePlan"
                        :item="item"
                        :condition="condition"
                    />

                    <!-- VEHICLE HEALTH CHECKS -->
                    <VehicleHealthCheckFeature
                        v-if="item?.type == WorkOrderType.VehicleHealthChecks || condition?.type == ConditionType.Service"
                        :care-plan="model.carePlan"
                        :item="item!"
                        :condition="condition"
                    />

                </div>            

            </div>
            
            <!-- MANAGE CONDITION -->
            <div v-if="conditionType != null"
                class="flex flex-col"
                ref="manageCondition">
            
                <!-- TAB / TITLE -->
                <div class="h-3 mr-auto pt-2 rounded-t-md 2xl:rounded-t-lg bg-gray-700">
                    <div class="h-4 w-4 bg-gray-700 absolute -right-4 bottom-0">
                        <div class="h-4 w-4 bg-gray-850 rounded-bl-[30%]" />
                    </div>
                    <h1 class="text-xs lg:text-base flex items-center gap-x-3 lg:gap-x-4 z-10
                            text-primary-500 dark:text-white px-4 py-1 tracking-widest">
                        <span class="uppercase 2xl:text-lg flex gap-3">
                            <Icon name="calendar" />
                            Reminder
                        </span>
                    </h1>
                </div>

                <!-- CONTENT -->
                <div class="gap-2 justify-center items-center
                            bg-gradient-to-b from-gray-700 to-gray-800
                            rounded-b-lg 2xl:rounded-b-xl
                            pt-5 rounded-tr-lg 2xl:rounded-tr-xl
                            text-sm">

                    <ConfigureCondition :condition-type="conditionType" />

                </div>            

            </div>

            <!-- FEATURED PRODUCT STATUS -->
            <div v-if="item != null && featuredProduct.isEnabled"
                class="flex flex-col">
                
                <div class="h-3 mr-auto pt-2 rounded-t-md 2xl:rounded-t-lg bg-gray-700">
                    <div class="h-4 w-4 bg-gray-700 absolute -right-4 bottom-0">
                        <div class="h-4 w-4 bg-gray-850 rounded-bl-[30%]" />
                    </div>
                    <h1 class="text-xs lg:text-base flex items-center gap-x-3 lg:gap-x-4 z-10
                            text-primary-500 dark:text-white px-4 py-1 tracking-widest">
                        <span class="uppercase 2xl:text-lg flex gap-3">
                            <Icon name="contract" />
                            {{ featuredProduct.title }}
                        </span>
                    </h1>
                </div>

                <!-- CONTENT -->
                <div v-if="item != null && featuredProduct.isEnabled"
                    class="gap-2 justify-center items-center
                            bg-gradient-to-b from-gray-700 to-gray-850
                            rounded-b-lg 2xl:rounded-b-xl
                            rounded-tr-lg 2xl:rounded-tr-xl
                            text-sm pb-4 pt-10 px-4">

                    <div class="flex items-start gap-2 p-1 text-base">
                        <div>Provided By</div>
                        <div class="flex-1 border-b-2 border-dotted opacity-60 h-[1.25rem]" />
                        <div>{{ featuredProduct.provider }}</div>
                    </div>  

                    <div class="flex items-start gap-2 p-1 text-base">
                        <div>Active From</div>
                        <div class="flex-1 border-b-2 border-dotted opacity-60 h-[1.25rem]" />
                        <div>{{ formatDate( featuredProduct.dateActiveFrom, 'DD-MMM-YYYY' ) }}</div>
                    </div>  

                    <div class="flex items-start gap-2 p-1 text-base">
                        <div>Expires</div>
                        <div class="flex-1 border-b-2 border-dotted opacity-60 h-[1.25rem]" />
                        <div>{{ formatDate( featuredProduct.dateActiveTo, 'DD-MMM-YYYY' ) }}</div>
                    </div>  

                    <div v-if="hasDocuments"
                        class="text-center mt-6 flex flex-col gap-4 pb-2">
                        
                        <JupiterButton 
                            v-if="termsAndConditionsLinkUrl"  
                            variant="outlined"
                            color="white/50"
                            type="url" 
                            :to="termsAndConditionsLinkUrl"
                            class="col-span-2 !h-10 lg:!h-12 3xl:!h-14">
                            <span class="flex items-center gap-1 text-white">
                                <Icon name="contract" class="text-2xl !hidden md:!block" />
                                <span class="3xl:text-base">
                                    Terms &amp; Conditions
                                </span>
                            </span>
                        </JupiterButton>

                        <JupiterButton 
                            v-if="documentsLinkUrl"
                            variant="outlined"
                            type="url" 
                            color="white/50"
                            class="col-span-2 !h-10 lg:!h-12 3xl:!h-14"
                            :to="documentsLinkUrl">
                            <span class="flex items-center gap-1 text-white">
                                <Icon name="contract" class="text-2xl !hidden md:!block" />
                                <span class="3xl:text-base">
                                    Documents
                                </span>
                            </span>
                        </JupiterButton>

                    </div>

                </div>
            </div>

        </div>

        <!-- REPAIR HISTORY -->
        <div 
            v-if="history.length > 0"
            class="order-10 rounded-lg 2xl:rounded-xl p-4 2xl:p-6">

            <h1 class="text-center text-lg 2xl:text-xl uppercase text-white/80">
                Repair History
            </h1>

            <div v-for="item in history"
                 class="p-1 
                        3xl:text-lg
                        items-center gap-2">

                <div class="flex items-center gap-4 text-base 2xl:text-lg">
                    {{ item.title ?? 'Repaired' }}
                    <span class="border-b-2 border-dotted flex-1 h-[1rem]"></span>
                    <span class="flex items-center gap-2">
                        <span class="uppercase text-sm">
                            {{ formatDate( item.date, 'DD-MMM-YYYY' ) }}
                        </span>
                        <span class="bg-secondary-500 rounded-full">
                            <Icon name="check" class="text-xl top-px" />
                        </span>
                    </span>
                </div>
                
            </div>
        </div>
    
    </div>

</template>